import type { SelectChangeEvent } from "@mui/material";
import classNames from "classnames";
import { type ChangeEvent, type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { StylistValue } from "../../../../services/api/instances/stylistApi/stylistTypes";
import { useAppDispatch } from "../../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../../services/redux/hooks/use-selector";
import { AppButton } from "../../../../shared/components/AppButton";
import { AppUnderlinedTitle } from "../../../../shared/components/AppUnderlinedTitle";
import { useStylist } from "../../hook/use-stylist";
import { getStylistAssistantsAction } from "../../redux/actions";
import {
  selectAssistants,
  selectSelectedAssistant,
  selectSelectedStylist,
} from "../../redux/selectors";
import { StylistNameChangeInput } from "../StylistNameChangeInput";
import { StylistTextArea } from "../StylistTextArea";
import { AssistantSelection } from "./components/AssistantSelection";
import styles from "./styles.module.css";
import { CopyButton } from "../../../../common/Components/Buttons/CopyButton";

interface Props {
  onClick: () => void;
  handleSelectAssistant: (event: SelectChangeEvent<unknown>) => void;
  className?: string;
}

export const StylistMain: FC<Props> = ({
  onClick,
  handleSelectAssistant,
  className,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const assistants = useAppSelector(selectAssistants);
  const selectedAssistant = useAppSelector(selectSelectedAssistant);
  const selectedStylist = useAppSelector(selectSelectedStylist);
  const { handleFieldChange, isResultLoading, stylistMode } = useStylist();
  const [minRows, setMinRows] = useState(4);

  useEffect(() => {
    dispatch(getStylistAssistantsAction());
    const calculateMinRows = () => {
      const windowHeight = window.innerHeight;
      const rowHeight = 32;
      const rows = Math.floor(windowHeight / rowHeight);
      setMinRows(rows);
    };

    calculateMinRows();

    window.addEventListener("resize", calculateMinRows);

    return () => {
      window.removeEventListener("resize", calculateMinRows);
    };
  }, []);

  return (
    <div className={classNames(styles.main, className)}>
      <AppUnderlinedTitle
        className={styles.title}
        leftContent={t("stylist.prompt")}
      />
      <StylistNameChangeInput
        onStylistNameChange={(event: ChangeEvent<HTMLInputElement>) =>
          handleFieldChange(event, "friendly_name", "name")
        }
      />
      <div className={styles["select-block-container"]}>
        <AssistantSelection
          assistants={assistants}
          label={t("stylist.label")}
          value={selectedAssistant?.name || ""}
          itemText={selectedAssistant?.name as string}
          placeholder={t("stylist.select_placeholder")}
          onChange={handleSelectAssistant}
        />
      </div>
      <div className={styles["text-area-container"]}>
        <StylistTextArea
          value={selectedStylist?.value.stylist_object.prompt as string}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            handleFieldChange(event, "prompt" as keyof StylistValue, "prompt")
          }
          placeholder={t("stylist.original_text_placeholder")}
          minRows={minRows}
        />
        <CopyButton
          copyContent={selectedStylist?.value.stylist_object.prompt as string}
          position="absolute"
        />
      </div>
      <AppButton
        buttonType="contained"
        disabled={isResultLoading || stylistMode === "auto"}
        className={styles["save-button"]}
        onClick={onClick}
      >
        {t("stylist.stylize_button")}
      </AppButton>
    </div>
  );
};
